import type { WidgetConfig, ChatMessage, StudentIdentity } from "../types";

/**
 * Widget API client — handles guest auth, config fetching, and conversation API.
 * Guest session is created lazily on first API call.
 */
export class WidgetClient {
  private apiBaseUrl: string;
  private token: string | null = null;
  private widgetKey: string | null = null;

  /**
   * A signed-in student's own token, held alongside the guest one.
   *
   * Both exist at once on purpose, and that is what makes anonymity real rather
   * than cosmetic. An anonymous conversation is created on the GUEST token, whose
   * session id is a random UUID with no connection to any account, so the server
   * stores no link between the student and what they said. Choosing to be
   * identified simply means using the other token. There is no "anonymous flag" on
   * an otherwise-attributed row that a future query could undo.
   */
  private userToken: string | null = null;
  private useUserToken = false;
  private chatModel: string | null = null;

  constructor(apiBaseUrl: string, widgetKey?: string) {
    this.apiBaseUrl = apiBaseUrl;
    this.widgetKey = widgetKey ?? null;
  }

  /** The token requests actually go out with. */
  private activeToken(): string | null {
    return this.useUserToken && this.userToken ? this.userToken : this.token;
  }

  setUserToken(token: string | null): void {
    this.userToken = token;
    if (!token) this.useUserToken = false;
  }

  getUserToken(): string | null {
    return this.userToken;
  }

  hasUserToken(): boolean {
    return !!this.userToken;
  }

  /** Identified when true; anonymous (guest token) when false. */
  setIdentified(on: boolean): void {
    this.useUserToken = on && !!this.userToken;
  }

  isIdentified(): boolean {
    return this.useUserToken && !!this.userToken;
  }

  /**
   * The student's model choice, sent with each message. Null means "whatever the
   * section is set to", so a student who never touches the picker follows the
   * instructor's default even if it changes mid-conversation.
   */
  setChatModel(key: string | null): void {
    this.chatModel = key;
  }

  /** Create a widget guest session (called lazily on first interaction) */
  async initGuestSession(key?: string): Promise<void> {
    const res = await fetch(`${this.apiBaseUrl}/widget/auth/guest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(key ? { key } : {}),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => null);
      const code = errBody?.error?.code;
      const message = errBody?.error?.message || `Failed to create guest session: ${res.status}`;
      const err = new Error(message) as Error & { code?: string; status?: number };
      err.code = code;
      err.status = res.status;
      throw err;
    }
    const json = await res.json();
    // Response is { data: { token, sessionId, widgetKeyId? } }
    this.token = json.data?.token ?? json.token;
  }

  /** Restore a previously saved JWT token */
  restoreToken(token: string): void {
    this.token = token;
  }

  /** Get the current JWT token (for saving to localStorage) */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Fetch widget configuration (validates UUIDs, returns display names).
   * In keyed mode the server derives scope from the JWT's widgetKeyId, so all
   * params are optional. In keyless mode courseId is required.
   */
  async getWidgetConfig(params: {
    courseId?: string;
    sectionId?: string;
    assignmentId?: string;
  } = {}): Promise<WidgetConfig> {
    const qs = new URLSearchParams();
    if (params.courseId) qs.set("courseId", params.courseId);
    if (params.sectionId) qs.set("sectionId", params.sectionId);
    if (params.assignmentId) qs.set("assignmentId", params.assignmentId);
    const query = qs.toString();
    const res = await this.authFetch(`/api/v1/widget/config${query ? `?${query}` : ""}`);
    const body = await res.json();
    return body.data ?? body;
  }

  /**
   * Cache lookup for page context — returns the id on a hit, null on a miss
   * (PAGE_CONTEXT_MISS) so the caller can upload the text.
   */
  async lookupPageContext(url: string, contentHash: string): Promise<string | null> {
    try {
      const res = await this.authFetch("/api/v1/widget/page-context", {
        method: "POST",
        body: JSON.stringify({ url, contentHash }),
      });
      const json = await res.json();
      return (json.data ?? json).pageContextId ?? null;
    } catch (err) {
      if ((err as any).status === 404) return null;
      throw err;
    }
  }

  /** Upload captured page text — server minifies and caches it. */
  async registerPageContext(url: string, pageText: string): Promise<string> {
    const res = await this.authFetch("/api/v1/widget/page-context", {
      method: "POST",
      body: JSON.stringify({ url, pageText }),
    });
    const json = await res.json();
    return (json.data ?? json).pageContextId;
  }

  /** Create a new conversation */
  async createConversation(body: {
    courseId: string;
    sectionId?: string;
    assignmentId?: string;
    conversationTypeId: string;
    title?: string;
    pageContextId?: string;
    studentName?: string;
    studentNumber?: string;
    pageUrl?: string;
    pageTitle?: string;
  }): Promise<{ id: string }> {
    const res = await this.authFetch("/api/v1/conversations", {
      method: "POST",
      body: JSON.stringify(body),
    });
    const json = await res.json();
    return json.data ?? json;
  }

  /**
   * Set or clear the current conversation's self-reported student identity.
   * Pass an identity to attribute the conversation; pass null to go anonymous.
   */
  async setIdentity(conversationId: string, identity: StudentIdentity | null): Promise<void> {
    await this.authFetch(`/api/v1/conversations/${conversationId}/identity`, {
      method: "PATCH",
      body: JSON.stringify({
        studentName: identity ? identity.studentName : null,
        studentNumber: identity ? identity.studentNumber : null,
      }),
    });
  }

  /**
   * Sign the student in with their UW NetID, in a popup.
   *
   * A popup rather than a redirect because the widget sits on a course page the
   * student is working through: sending the whole tab to an identity provider and
   * back would lose their place, and on an assignment page that is the page.
   *
   * MUST be called synchronously from a click handler. Browsers block popups opened
   * after an await, so the window is opened first and the URL assigned immediately.
   *
   * The server decides the postMessage target from the widget key's own allowed
   * domains, not from anything sent here, so a page that is not on that list cannot
   * receive a token even if it manages to open this flow.
   */
  signInWithUwNetId(): Promise<{ displayName: string; netid: string | null } | null> {
    const popup = window.open("", "uwt-tutor-signin", "width=520,height=640");
    if (!popup) return Promise.resolve(null); // blocked; caller tells the student

    const params = new URLSearchParams({
      mode: "widget",
      origin: window.location.origin,
      ...(this.widgetKey ? { key: this.widgetKey } : {}),
    });
    popup.location.href = `${this.apiBaseUrl}/api/auth/oauth2/authorize?${params.toString()}`;

    return new Promise((resolve) => {
      let settled = false;
      const finish = (value: { displayName: string; netid: string | null } | null) => {
        if (settled) return;
        settled = true;
        window.removeEventListener("message", onMessage);
        clearInterval(closedPoll);
        resolve(value);
      };

      const onMessage = (event: MessageEvent) => {
        // Only trust messages from the backend's own origin, and only our shape.
        if (event.origin !== new URL(this.apiBaseUrl).origin) return;
        const d = event.data;
        if (!d || d.source !== "uwt-tutor-widget-auth") return;

        if (d.token) {
          this.setUserToken(d.token);
          this.setIdentified(true);
          finish({ displayName: d.displayName ?? "", netid: d.netid ?? null });
        } else {
          finish(null);
        }
      };
      window.addEventListener("message", onMessage);

      // A student who closes the popup mid-flow should not leave a promise hanging
      // and a spinner running forever.
      const closedPoll = setInterval(() => {
        if (popup.closed) finish(null);
      }, 500);

      setTimeout(() => finish(null), 3 * 60 * 1000);
    });
  }

  /**
   * Relay a staff budget alert the server could not send.
   *
   * The backend is blocked from the form relay by Cloudflare, which challenges
   * non-browser requests; a browser is exactly what it expects. Costs one request,
   * at most twice a month, on whichever session happens to load after a threshold
   * is crossed — not every student, not every load.
   *
   * Entirely silent. Failures are swallowed and the alert stays pending for the
   * next load: this is not the student's errand and must never surface to them or
   * interfere with their chat.
   */
  async relayBudgetAlert(alert: NonNullable<WidgetConfig["pendingAlert"]>): Promise<void> {
    try {
      const form = new FormData();
      form.append("access_key", alert.accessKey);
      form.append("subject", alert.subject);
      // from_name comes from the server and must match the relay's configured
      // value: anything else is accepted with success:true and then dropped.
      form.append("from_name", alert.fromName);
      form.append("page", "UWT Tutor AI widget");
      form.append("message", alert.body);

      const res = await fetch("https://api.web3forms.com/submit", { method: "POST", body: form });
      const json = await res.json().catch(() => null);
      if (!json?.success) return;

      // Acknowledge only on confirmed delivery, so a failure leaves the alert
      // pending rather than silently consuming the month's warning.
      await this.authFetch("/api/v1/widget/budget-alert-ack", {
        method: "POST",
        body: JSON.stringify({ threshold: alert.threshold, nonce: alert.nonce }),
      });
    } catch {
      /* Swallowed deliberately — see above. */
    }
  }

  /**
   * Fetch conversation with message history. Also returns the identity currently
   * stored on the row so a restored session can reconcile it against localStorage.
   */
  async getConversation(conversationId: string): Promise<{
    messages: ChatMessage[];
    studentName: string | null;
    studentNumber: string | null;
  }> {
    const res = await this.authFetch(`/api/v1/conversations/${conversationId}`);
    const json = await res.json();
    const data = json.data ?? json;
    const messages: ChatMessage[] = (data.messages || []).map((m: any) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      createdAt: m.createdAt,
    }));
    return {
      messages,
      studentName: data.studentName ?? null,
      studentNumber: data.studentNumber ?? null,
    };
  }

  /** Send a message — returns raw Response for SSE streaming */
  sendMessage(conversationId: string, content: string, signal?: AbortSignal): Promise<Response> {
    return this.authFetch(
      `/api/v1/conversations/${conversationId}/messages`,
      {
        method: "POST",
        body: JSON.stringify({ content, ...(this.chatModel ? { model: this.chatModel } : {}) }),
        signal,
      }
    );
  }

  /** Authenticated fetch — lazily inits guest session if needed */
  private async authFetch(path: string, init?: RequestInit): Promise<Response> {
    // The guest session is still created even when signed in: it is what anonymous
    // conversations run on, and a student can switch back at any time.
    if (!this.token) {
      await this.initGuestSession(this.widgetKey ?? undefined);
    }

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${this.activeToken()}`,
    };

    const res = await fetch(`${this.apiBaseUrl}${path}`, {
      ...init,
      headers: { ...headers, ...(init?.headers as Record<string, string>) },
    });

    if (!res.ok && res.status !== 200) {
      const errBody = await res.json().catch(() => null);
      const err = new Error(errBody?.error?.message || `Widget API error: ${res.status}`) as Error & {
        status?: number;
        code?: string;
      };
      err.status = res.status;
      err.code = errBody?.error?.code;
      throw err;
    }

    return res;
  }
}
